import { TextInput } from '@components/ui/Input';
import DatePicker from '@components/ui/DatePicker/DatePicker';
import Select from '@workpay/ds-shared/components/ui/Select';
import { isEffectivelyLocked } from '@workpay/ds-shared/utils/fieldLock';
import {
  parsePrefixOptions,
  splitPrefixedValue,
  joinPrefixedValue,
} from '@workpay/ds-shared/utils/fieldPrefix';
import { CURRENCIES } from '@workpay/ds-shared/utils/currencies';
import { COUNTRY_CODES } from '@workpay/ds-shared/utils/countryCodes';
import type { TemplateSigningField } from '@workpay/ds-shared/types/template';
import { useTranslation } from '@hooks/useTranslation';
import styles from './PrefilledAuthorFieldInput.module.scss';

interface Props {
  field: TemplateSigningField;
  /** Current value from `prefilledFieldValues[key]` — undefined when nothing seeded. */
  value: string | undefined;
  /** Called with the fully-composed value to write back into wizard state. */
  onChange: (newValue: string) => void;
}

/**
 * Renders the input control for a single AUTHOR-prefilled field on the
 * Fill Placeholders step of the envelope wizard.
 *
 * Three render paths:
 *   - CHECKBOX → labelled checkbox + locked hint
 *   - CURRENCY / PHONE → prefix select (or static label if locked) + body input
 *   - everything else → generic TextInput
 *
 * Lock semantics use `isEffectivelyLocked` so stale records like
 * `{ isReadOnly: true, defaultValue: '' }` can't trap the user.
 */
export function PrefilledAuthorFieldInput({ field, value, onChange }: Props) {
  const { t } = useTranslation();
  const ft = field.fieldType;
  const locked = isEffectivelyLocked(field);
  const label = field.label && field.label !== field.fieldType ? field.label : field.fieldType;

  const INPUT_TYPE_MAP: Record<string, string> = {
    DATE: 'date',
    DATE_SIGNED: 'date',
    NUMBER: 'text',
    CURRENCY: 'text',
    EMAIL: 'email',
    PHONE: 'tel',
  };
  const INPUT_MODE_MAP: Record<string, string> = {
    NUMBER: 'decimal',
    CURRENCY: 'decimal',
    PHONE: 'tel',
    EMAIL: 'email',
  };
  const inputType = INPUT_TYPE_MAP[ft] ?? 'text';
  const inputMode = INPUT_MODE_MAP[ft] as 'decimal' | 'tel' | 'email' | undefined;

  const PLACEHOLDER_MAP: Record<string, string> = {
    DATE: t('K_DS_PLACEHOLDER_SELECT_DATE', 'Select date'),
    DATE_SIGNED: t('K_DS_PLACEHOLDER_SELECT_DATE', 'Select date'),
    NUMBER: t('K_DS_PLACEHOLDER_ENTER_NUMBER', 'Enter number'),
    CURRENCY: t('K_DS_PLACEHOLDER_ENTER_AMOUNT', 'Enter amount'),
    EMAIL: t('K_DS_PLACEHOLDER_ENTER_EMAIL', 'Enter email address'),
    PHONE: t('K_DS_PLACEHOLDER_ENTER_PHONE', 'Enter phone number'),
    CHECKBOX: 'true / false',
  };
  const inputPlaceholder = PLACEHOLDER_MAP[ft] ?? t('K_DS_PLACEHOLDER_ENTER_VALUE', 'Enter value');

  const lockedHint = t('K_DS_LABEL_LOCKED', '(locked)');

  if (ft === 'CHECKBOX') {
    return (
      <label className={styles.checkboxLabel}>
        <input
          type="checkbox"
          checked={value === 'true'}
          disabled={locked}
          onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
        />
        {label}
        {locked && <span className={styles.lockedHint}>{lockedHint}</span>}
      </label>
    );
  }

  if (ft === 'DROPDOWN') {
    // Options come from the template's `dropdownOptions` — convention is a
    // comma-separated string, matching how SigningView parses signer-side
    // dropdowns at sign time.
    const options = (field.dropdownOptions ?? '')
      .split(',')
      .map((opt) => opt.trim())
      .filter(Boolean);
    const selectOptions = options.map((opt) => ({ value: opt, label: opt }));
    const composedLabel = locked ? `${label} ${lockedHint}` : label;
    return (
      <div className={styles.prefixedField}>
        <label className={styles.prefixedFieldLabel}>{composedLabel}</label>
        {locked ? (
          // When locked, show the chosen value as a static chip instead of a disabled Select
          // so the user can never accidentally "tap to change" a value the template has frozen.
          <span className={styles.prefixedFieldPrefix}>{value || field.defaultValue || '—'}</span>
        ) : (
          <Select
            options={selectOptions}
            value={value ?? ''}
            onChange={(v) => onChange(v)}
            placeholder={t('K_PLACEHOLDER_SELECT', 'Select...')}
            searchable={selectOptions.length > 6}
          />
        )}
      </div>
    );
  }

  if (ft === 'CURRENCY' || ft === 'PHONE') {
    const fallbackPrefix = ft === 'CURRENCY' ? 'USD' : '+1';
    const { prefix: optionsPrefix, locked: prefixLocked } = parsePrefixOptions(
      field.dropdownOptions,
      fallbackPrefix,
    );
    const { prefix: storedPrefix, body } = splitPrefixedValue(ft, value ?? '');
    const prefix = storedPrefix || optionsPrefix;
    const composedLabel = locked ? `${label} ${lockedHint}` : label;

    const prefixEditable = !locked && !prefixLocked;
    const prefixOptions = ft === 'CURRENCY' ? CURRENCIES : COUNTRY_CODES;
    const prefixSearchPlaceholder =
      ft === 'CURRENCY'
        ? t('K_DS_PLACEHOLDER_SEARCH_CURRENCY', 'Search currency...')
        : t('K_DS_PLACEHOLDER_SEARCH_COUNTRY', 'Search country...');

    const staticPrefixVariant = locked
      ? styles.prefixedFieldPrefixRowLocked
      : prefixLocked
        ? styles.prefixedFieldPrefixOnlyLocked
        : '';

    const writeValue = (newPrefix: string, newBody: string) => {
      if (newBody.trim()) {
        onChange(joinPrefixedValue(newPrefix, newBody));
      } else {
        onChange(`${newPrefix} `);
      }
    };

    return (
      <div className={styles.prefixedField}>
        <label className={styles.prefixedFieldLabel}>{composedLabel}</label>
        <div
          className={`${styles.prefixedFieldRow} ${locked ? styles.prefixedFieldRowReadonly : ''}`}
        >
          {prefixEditable ? (
            <div className={styles.prefixedFieldPrefixSelect}>
              <Select
                options={prefixOptions}
                value={prefix}
                onChange={(newPrefix) => writeValue(newPrefix, body)}
                placeholder={prefixSearchPlaceholder}
                searchable
              />
            </div>
          ) : (
            <span className={`${styles.prefixedFieldPrefix} ${staticPrefixVariant}`}>
              {prefixLocked && !locked && (
                <span className={styles.prefixedFieldPrefixLockIcon} aria-hidden="true">
                  &#128274;
                </span>
              )}
              {prefix}
            </span>
          )}
          <input
            className={styles.prefixedFieldInput}
            type="text"
            inputMode={ft === 'CURRENCY' ? 'decimal' : 'tel'}
            value={body}
            onChange={(e) => writeValue(prefix, e.target.value)}
            placeholder={inputPlaceholder}
            readOnly={locked}
          />
        </div>
      </div>
    );
  }

  // DATE / DATE_SIGNED → design-system DatePicker (calendar popover + keyboard nav
  // via react-aria), not the native browser picker.
  if (ft === 'DATE' || ft === 'DATE_SIGNED') {
    return (
      <DatePicker
        label={locked ? `${label} ${lockedHint}` : label}
        value={value ?? ''}
        onChange={(v) => onChange(v)}
        placeholder={inputPlaceholder}
        disabled={locked}
      />
    );
  }

  return (
    <TextInput
      label={locked ? `${label} ${lockedHint}` : label}
      type={inputType}
      inputMode={inputMode}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={inputPlaceholder}
      readOnly={locked}
      fullWidth
    />
  );
}
